import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Chip } from '@mui/material';
import { Stadium as StadiumIcon, SportsSoccer as BallIcon } from '@mui/icons-material';
import { COLORS } from './MAMCStyles';
import { styled } from '@mui/material/styles';

// Styled card for finished knockout matches
const FinishedMatchCard = styled(Card)(({ theme }) => ({
  backgroundColor: COLORS.background.light,
  color: COLORS.text.primary,
  boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
  borderRadius: theme.spacing(1.5),
  border: `1px solid rgba(218,165,32,0.1)`,
  transition: 'all 0.3s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 24px rgba(218,165,32,0.3)',
    border: `1px solid rgba(218,165,32,0.3)`,
  },
}));

const FinishedKnockout = ({ matches, teams, scores, scorers }) => {
  // Filter for finished knockout matches (not group stage, and finished)
  const finishedKnockoutMatches = matches
    .filter(match => match.isFinished && match.type && match.type !== 'Group stage')
    .sort((a, b) => {
      // Sort by match importance (Final > Semi Final > Quarter Final > Round of 16)
      const typeOrder = {
        Final: 1,
        'Semi Final': 2,
        'Quarter Final': 3,
        'Round of 16': 4,
      };
      const orderA = typeOrder[a.type] || 5;
      const orderB = typeOrder[b.type] || 5;

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      // If same type, sort by date (most recent first)
      return new Date(b.time) - new Date(a.time);
    });

  if (finishedKnockoutMatches.length === 0) return null;

  const formatMatchTime = timeString => {
    const date = new Date(timeString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Box
      sx={{
        mb: { xs: 2, sm: 3, md: 4 },
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          pb: 2,
          borderBottom: `2px solid ${COLORS.background.light}`,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: COLORS.text.accent,
            fontSize: { xs: '1rem', sm: '1.25rem' },
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontWeight: 'bold',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
          }}
        >
          Knockout results
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {finishedKnockoutMatches.map(match => {
          const homeTeam = teams.find(t => t.id === match.home_team_id);
          const awayTeam = teams.find(t => t.id === match.away_team_id);
          const score = scores.find(s => s.match_id === match.id);
          const matchScorers = scorers.filter(s => s.match_id === match.id);

          // Filter scorers by team
          const homeTeamScorers = matchScorers
            .filter(scorer => homeTeam?.players?.some(player => player.id === scorer.player_id))
            .sort((a, b) => a.score_minute - b.score_minute);

          const awayTeamScorers = matchScorers
            .filter(scorer => awayTeam?.players?.some(player => player.id === scorer.player_id))
            .sort((a, b) => a.score_minute - b.score_minute);

          return (
            <Grid item xs={12} sm={6} md={4} key={match.id}>
              <FinishedMatchCard>
                <CardContent
                  sx={{
                    p: 2,
                    '&:last-child': { pb: 2 },
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: '320px',
                  }}
                >
                  {/* Match Type Badge */}
                  <Chip
                    label={match.type}
                    size="small"
                    sx={{
                      background: COLORS.gradient.gold,
                      color: COLORS.background.dark,
                      fontWeight: 'bold',
                      fontSize: '0.7rem',
                      mb: 1.5,
                      width: 'fit-content',
                    }}
                  />

                  {/* Score Display */}
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        color: COLORS.text.primary,
                        fontWeight: 'bold',
                        fontSize: '1.5rem',
                        mb: 1,
                      }}
                    >
                      {score ? `${score.home_team_score} - ${score.away_team_score}` : '0 - 0'}
                    </Typography>

                    {/* Teams */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: COLORS.text.secondary,
                        fontSize: '0.875rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {homeTeam?.team_name || 'TBD'} vs {awayTeam?.team_name || 'TBD'}
                    </Typography>
                  </Box>

                  {/* Scorers Summary */}
                  {(homeTeamScorers.length > 0 || awayTeamScorers.length > 0) && (
                    <Box
                      sx={{
                        mb: 'auto',
                        minHeight: '60px',
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 0.5,
                        alignItems: 'start',
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                        {homeTeamScorers.map((scorer, index) => (
                          <Typography
                            key={`home-${index}`}
                            variant="caption"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: COLORS.text.secondary,
                              fontSize: '0.7rem',
                              overflow: 'hidden',
                            }}
                          >
                            <BallIcon
                              sx={{ fontSize: 10, color: COLORS.secondary, flexShrink: 0 }}
                            />
                            <span
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {scorer.player?.player_name || 'Unknown'} ({scorer.score_minute}')
                            </span>
                          </Typography>
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                        {awayTeamScorers.map((scorer, index) => (
                          <Typography
                            key={`away-${index}`}
                            variant="caption"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              gap: 0.5,
                              color: COLORS.text.secondary,
                              fontSize: '0.7rem',
                              overflow: 'hidden',
                            }}
                          >
                            <span
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                textAlign: 'right',
                              }}
                            >
                              {scorer.player?.player_name || 'Unknown'} ({scorer.score_minute}')
                            </span>
                            <BallIcon
                              sx={{ fontSize: 10, color: COLORS.secondary, flexShrink: 0 }}
                            />
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Match Details */}
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: COLORS.text.secondary,
                        fontSize: '0.75rem',
                      }}
                    >
                      {formatMatchTime(match.time)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <StadiumIcon sx={{ fontSize: 14, color: COLORS.text.accent }} />
                      <Typography
                        variant="caption"
                        sx={{
                          color: COLORS.text.accent,
                          fontSize: '0.75rem',
                          fontWeight: '500',
                        }}
                      >
                        Field {match.field}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </FinishedMatchCard>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default FinishedKnockout;
